import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBranding } from "../context/BrandingContext";

export interface FallbackFormData {
  device_type: string;
  apartment: string;
  since: string;
  email: string;
  identification_method: "matched" | "fallback";
  speedtest_status: string | null; // "matched" | "no_match" | "error" | null
  privacy_accepted: boolean;
  hp_field?: string; // AUDIT-SEC-2/S2: honeypot – always empty for real users
}

export interface CaseSubmission extends FallbackFormData {
  description: string;
  screenshot: File | null;
}

interface FallbackFormProps {
  speedtestResult?: { correlation_status: string } | null;
  onSubmit: (data: FallbackFormData) => void;
  onBack: () => void;
}

export function FallbackForm({ speedtestResult, onSubmit, onBack }: FallbackFormProps) {
  const { t } = useTranslation();
  const branding = useBranding();

  const [deviceType, setDeviceType] = useState("");
  const [apartment, setApartment] = useState("");
  const [since, setSince] = useState("today");
  const [email, setEmail] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ deviceType?: boolean; email?: boolean; privacy?: boolean }>({});
  // AUDIT-SEC-2/S2: honeypot – bound to state so the DOM value is captured on submit
  const [hpField, setHpField] = useState("");

  const deviceOptions = ["phone", "laptop", "tablet", "tv", "other"] as const;
  const sinceOptions = ["today", "yesterday", "week", "longer"] as const;

  const isMatched = speedtestResult?.correlation_status === "matched";

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const errors = {
      deviceType: !deviceType,
      email: !email,
      privacy: !privacyAccepted,
    };
    if (errors.deviceType || errors.email || errors.privacy) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const data: FallbackFormData = {
      device_type: deviceType,
      apartment,
      since,
      email,
      identification_method: isMatched ? "matched" : "fallback",
      speedtest_status: speedtestResult?.correlation_status ?? null,
      privacy_accepted: privacyAccepted,
      hp_field: hpField,
    };

    onSubmit(data);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          {t("fallback_title")}
        </h1>
        <p className="text-gray-500 text-sm mb-6">{t("fallback_intro")}</p>

        {speedtestResult && (
          <div
            className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-block mb-6 ${
              isMatched
                ? "bg-green-100 text-green-700"
                : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {isMatched
              ? t("fallback_identification_auto")
              : t("fallback_identification_fallback")}
          </div>
        )}

        {/* AUDIT-SEC-2/S2: honeypot – hidden from real users, bots fill it */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
          <label htmlFor="hp_website">Website</label>
          <input
            type="text"
            id="hp_website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={hpField}
            onChange={(e) => setHpField(e.target.value)}
          />
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Device type */}
          <fieldset className="mb-6">
            <legend className="text-sm font-medium text-gray-700 mb-2">
              {t("fallback_device_type")} *
            </legend>
            <div className="flex flex-col gap-2">
              {deviceOptions.map((v) => (
                <label
                  key={v}
                  className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="device_type"
                    value={v}
                    checked={deviceType === v}
                    onChange={() => { setDeviceType(v); setFieldErrors((e) => ({ ...e, deviceType: false })); }}
                    className="accent-blue-600"
                  />
                  {t(`fallback_device_${v}`)}
                </label>
              ))}
            </div>
            {fieldErrors.deviceType && (
              <p role="alert" className="text-red-600 text-xs mt-1">{t("fallback_required_field")}</p>
            )}
          </fieldset>

          {/* Apartment */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("fallback_apartment")} *
            </label>
            <input
              type="text"
              value={apartment}
              onChange={(e) => setApartment(e.target.value)}
              placeholder={t("fallback_apartment_placeholder")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Since when */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("fallback_since")}
            </label>
            <select
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {sinceOptions.map((v) => (
                <option key={v} value={v}>
                  {t(`fallback_since_${v}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Email */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("fallback_email")} *
            </label>
            <input
              type="email"
              value={email}
              required
              onChange={(e) => { setEmail(e.target.value); setFieldErrors((err) => ({ ...err, email: false })); }}
              placeholder={t("fallback_email_placeholder")}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.email ? "border-red-400" : "border-gray-300"}`}
            />
            {fieldErrors.email && (
              <p role="alert" className="text-red-600 text-xs mt-1">{t("fallback_required_field")}</p>
            )}
          </div>

          {/* Privacy */}
          <div className="mb-6">
            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => { setPrivacyAccepted(e.target.checked); setFieldErrors((err) => ({ ...err, privacy: false })); }}
                className="mt-0.5 accent-blue-600"
              />
              <span>
                {branding.privacy_url ? (
                  <>
                    {t("fallback_privacy_pre")}{" "}
                    <a href={branding.privacy_url} target="_blank" rel="noopener noreferrer" className="underline text-blue-600">
                      {t("fallback_privacy_link")}
                    </a>
                    {t("fallback_privacy_post")}
                  </>
                ) : (
                  t("fallback_privacy")
                )}{" "}*
              </span>
            </label>
            {fieldErrors.privacy && (
              <p role="alert" className="text-red-600 text-xs mt-1 ml-6">{t("fallback_required_field")}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              type="submit"
              className="w-full py-3 px-4 btn-primary rounded-xl font-medium transition-colors"
            >
              {t("fallback_submit")}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="w-full py-3 px-4 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              {t("step_prev")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
